/* NORDAEN — header behaviour.
   Condense-on-scroll via a sentinel (no per-frame scroll handler), and a
   mobile panel with a proper focus trap. */

(() => {
  const init = () => {
    const header = document.querySelector('[data-n-header]');
    if (!header) return;

    /* ---- Condense ------------------------------------------------
       A 1px sentinel sits at the top of the document. While it is in
       view we are over the hero; once it leaves, condense. Costs one
       IO callback per crossing rather than work on every scroll tick. */
    if (!header.classList.contains('is-solid')) {
      const sentinel = document.querySelector('.n-header-sentinel');
      if (sentinel && 'IntersectionObserver' in window) {
        new IntersectionObserver(
          ([entry]) => header.classList.toggle('is-condensed', !entry.isIntersecting),
          { threshold: 0 }
        ).observe(sentinel);
      } else {
        header.classList.add('is-condensed');
      }
    }

    /* ---- Mobile panel -------------------------------------------- */
    const toggle = header.querySelector('[data-n-menu-toggle]');
    const menu = document.querySelector('[data-n-menu]');

    /* ---- Objects inside a line -----------------------------------
       Bound per button, and ahead of the guard below, so that a section
       reload in the editor picks up new chevrons even when the panel
       itself has already been bound. */
    if (menu) {
      menu.querySelectorAll('[data-n-menu-more]').forEach((more) => {
        if (more.dataset.nBound) return;
        more.dataset.nBound = '1';

        const panel = document.getElementById(more.getAttribute('aria-controls'));
        const row = more.closest('li');
        if (!panel || !row) return;

        const label = row.querySelector('.n-menu__label');
        const name = label ? label.textContent.trim() : 'this line';

        more.addEventListener('click', () => {
          const open = more.getAttribute('aria-expanded') === 'true';
          more.setAttribute('aria-expanded', open ? 'false' : 'true');
          more.setAttribute('aria-label', (open ? 'Show the objects in ' : 'Hide the objects in ') + name);
          panel.hidden = open;
          row.classList.toggle('is-open', !open);
        });
      });
    }

    if (!toggle || !menu || toggle.dataset.nBound) return;
    toggle.dataset.nBound = '1';

    const FOCUSABLE = 'a[href], button:not([disabled])';
    let lastFocused = null;

    const isOpen = () => toggle.getAttribute('aria-expanded') === 'true';

    const open = () => {
      lastFocused = document.activeElement;
      menu.hidden = false;
      // Next frame, so the opacity transition has a start value to run from.
      requestAnimationFrame(() => menu.classList.add('is-open'));
      toggle.setAttribute('aria-expanded', 'true');
      toggle.setAttribute('aria-label', 'Close menu');
      document.documentElement.style.overflow = 'hidden';
      const first = menu.querySelector(FOCUSABLE);
      if (first) first.focus();
    };

    const close = ({ restoreFocus = true } = {}) => {
      menu.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      toggle.setAttribute('aria-label', 'Open menu');
      document.documentElement.style.overflow = '';

      const finish = () => { menu.hidden = true; };
      const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduced) finish();
      else menu.addEventListener('transitionend', finish, { once: true });

      if (restoreFocus && lastFocused) lastFocused.focus();
    };

    toggle.addEventListener('click', () => (isOpen() ? close() : open()));

    // Escape closes; Tab is trapped inside the panel while it is open.
    document.addEventListener('keydown', (e) => {
      if (!isOpen()) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
      }
      if (e.key !== 'Tab') return;

      /* A collapsed object list is still in the document. Only what is on
         screen can take focus, or the trap wraps to something invisible. */
      const items = [...menu.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
      items.push(toggle);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    // Navigating away should not leave the panel latched open.
    menu.querySelectorAll('a[href]').forEach((a) =>
      a.addEventListener('click', () => close({ restoreFocus: false }))
    );

    // Returning to desktop width mid-session.
    matchMedia('(min-width: 900px)').addEventListener('change', (e) => {
      if (e.matches && isOpen()) close({ restoreFocus: false });
    });
  };

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init, { once: true });

  document.addEventListener('shopify:section:load', init);
})();
